import type { KbArticle, KbArticlesResponse, ShipstarConfig } from "./types";
import { escapeHtml } from "./utils";

function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function groupByCategory(articles: KbArticle[]): { category: string; articles: KbArticle[] }[] {
  const map = new Map<string, KbArticle[]>();
  for (const article of articles) {
    const cat = article.category || "General";
    if (!map.has(cat)) map.set(cat, []);
    map.get(cat)!.push(article);
  }
  return Array.from(map, ([category, articles]) => ({ category, articles }));
}

function highlightMatch(text: string, query: string): string {
  if (!query) return escapeHtml(text);
  const escaped = escapeHtml(text);
  const escapedQuery = escapeHtml(query);
  const regex = new RegExp(`(${escapedQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  return escaped.replace(regex, "<mark>$1</mark>");
}

const shipstarIcon = `<svg width="14" height="14" viewBox="0 0 195 195" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M123.215 24.3304C125.235 21.8047 129.145 21.8047 128.921 24.3304L124.507 73.9972C124.406 75.1267 125.218 75.8915 126.518 75.8915H183.666C186.572 75.8915 186.27 79.2898 183.225 80.8508L123.349 111.547C121.987 112.245 120.997 113.482 120.897 114.612L116.482 164.278C116.258 166.804 112.161 168.904 110.504 167.343L77.9123 136.648C77.1711 135.949 75.7473 135.949 74.3856 136.648L14.5092 167.343C11.4643 168.904 9.23429 166.804 11.2549 164.278L50.9888 114.612C51.8924 113.482 52.0024 112.245 51.2612 111.547L18.6698 80.8508C17.0125 79.2898 19.7312 75.8915 22.6373 75.8915H79.7856C81.0852 75.8915 82.577 75.1267 83.4806 73.9972L123.215 24.3304Z" fill="currentColor"/></svg>`;

const searchIcon = `<svg class="ss-kb-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>`;

const bookIcon = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>`;

const arrowLeftIcon = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>`;

// ---------------------------------------------------------------------------
// List view: article card for the listing page
// ---------------------------------------------------------------------------

function renderArticleCard(article: KbArticle, query: string): string {
  const slug = slugify(article.title);
  const titleHtml = article.title
    ? highlightMatch(article.title, query)
    : "";

  // Truncate content preview to ~120 chars
  const preview = article.content.length > 120
    ? article.content.slice(0, 120).replace(/\s+\S*$/, "") + "..."
    : article.content;
  const contentHtml = highlightMatch(preview, query);

  return `<a class="ss-kb-card" href="#" data-article-slug="${escapeHtml(slug)}">
    <div class="ss-kb-icon">${bookIcon}</div>
    <div class="ss-kb-card-body">
      <h3 class="ss-kb-card-title">${titleHtml}</h3>
      <p class="ss-kb-card-preview">${contentHtml}</p>
    </div>
  </a>`;
}

function renderArticleGrid(container: HTMLElement, articles: KbArticle[], query: string): void {
  if (articles.length === 0) {
    container.innerHTML = `<div class="ss-kb-no-results">No articles match your search.</div>`;
    return;
  }

  const groups = groupByCategory(articles);
  container.innerHTML = groups
    .map(({ category, articles: groupArticles }) => {
      const cardsHtml = groupArticles.map((a) => renderArticleCard(a, query)).join("");
      return `<div class="ss-kb-section">
        <h3 class="ss-kb-category">${escapeHtml(category)} <span class="ss-kb-category-count">${groupArticles.length}</span></h3>
        <div class="ss-kb-grid">${cardsHtml}</div>
      </div>`;
    })
    .join("");
}

// ---------------------------------------------------------------------------
// Detail view: full article page
// ---------------------------------------------------------------------------

function renderArticleDetail(article: KbArticle, allArticles: KbArticle[]): string {
  // Related articles: same category first, then others, max 3
  const others = allArticles.filter((a) => a.id !== article.id);
  const sameCategory = others.filter((a) => a.category === article.category);
  const diffCategory = others.filter((a) => a.category !== article.category);
  const related = [...sameCategory, ...diffCategory].slice(0, 3);

  const categoryBadge = article.category
    ? `<span class="ss-kb-detail-badge">${bookIcon} ${escapeHtml(article.category)}</span>`
    : "";

  const relatedHtml = related.length > 0
    ? `<div class="ss-kb-related">
        <div class="ss-kb-related-divider"></div>
        <h3 class="ss-kb-related-heading">Related Articles</h3>
        <div class="ss-kb-related-grid">
          ${related.map((a) => {
            const slug = slugify(a.title);
            return `<a class="ss-kb-related-card" href="#" data-article-slug="${escapeHtml(slug)}">
              <div class="ss-kb-icon ss-kb-icon-sm">${bookIcon}</div>
              <div class="ss-kb-related-card-body">
                <h4 class="ss-kb-related-title">${escapeHtml(a.title)}</h4>
                ${a.category ? `<span class="ss-kb-related-cat">${escapeHtml(a.category)}</span>` : ""}
              </div>
            </a>`;
          }).join("")}
        </div>
      </div>`
    : "";

  return `<div class="ss-kb-detail">
    <div class="ss-kb-detail-nav">
      <button class="ss-kb-back" type="button">
        ${arrowLeftIcon}
        <span>Back to Knowledge Base</span>
      </button>
      <div class="ss-kb-detail-search">
        ${searchIcon}<input type="text" class="ss-kb-detail-search-input" placeholder="Search articles..." />
      </div>
    </div>
    ${categoryBadge}
    <h1 class="ss-kb-detail-title">${escapeHtml(article.title)}</h1>
    <div class="ss-kb-detail-content">${escapeHtml(article.content)}</div>
    ${relatedHtml}
  </div>`;
}

// ---------------------------------------------------------------------------
// Static render (no search, no navigation) — kept for backwards compat
// ---------------------------------------------------------------------------

function renderArticleStatic(article: KbArticle): string {
  const titleHtml = article.title
    ? `<h3 class="ss-kb-title">${escapeHtml(article.title)}</h3>`
    : "";
  const contentHtml = article.content
    ? `<p class="ss-kb-content">${escapeHtml(article.content)}</p>`
    : "";

  return `<article class="ss-kb-article">
    <div class="ss-kb-icon">${bookIcon}</div>
    <div class="ss-kb-body">
      ${titleHtml}
      ${contentHtml}
    </div>
  </article>`;
}

/**
 * Renders a static KB widget (no search, no navigation). Used when search is disabled.
 */
export function renderKbWidget(
  data: KbArticlesResponse,
  config: ShipstarConfig
): string {
  if (!data.articles || data.articles.length === 0) {
    return `<div class="ss-widget ss-kb"><div class="ss-empty">No knowledge base articles available.</div></div>`;
  }

  const articles = data.articles.slice(0, config.maxItems);
  const groups = groupByCategory(articles);

  const sectionsHtml = groups
    .map(({ category, articles: groupArticles }) => {
      const articlesHtml = groupArticles.map((a) => renderArticleStatic(a)).join("");
      return `<div class="ss-kb-section">
        <h3 class="ss-kb-category">${escapeHtml(category)}</h3>
        <div class="ss-kb-grid">
          ${articlesHtml}
        </div>
      </div>`;
    })
    .join("");

  const footerHtml = config.branding
    ? `<div class="ss-footer">
    <a href="https://shipstar.ai" target="_blank" rel="noopener noreferrer">${shipstarIcon} Powered by Shipstar</a>
  </div>`
    : "";

  return `<div class="ss-widget ss-kb">
    <div class="ss-kb-header">
      <h2 class="ss-kb-heading">Knowledge Base</h2>
    </div>
    ${sectionsHtml}
    ${footerHtml}
  </div>`;
}

// ---------------------------------------------------------------------------
// Interactive mount: search + sub-page navigation inside Shadow DOM
// ---------------------------------------------------------------------------

/**
 * Mounts an interactive KB widget with search and article sub-pages into
 * the Shadow DOM. Clicking an article card navigates to a detail view;
 * the back button returns to the listing.
 */
export function mountKbWidget(
  shadowRoot: ShadowRoot,
  data: KbArticlesResponse,
  config: ShipstarConfig
): void {
  if (!data.articles || data.articles.length === 0) {
    const empty = document.createElement("div");
    empty.innerHTML = `<div class="ss-widget ss-kb"><div class="ss-empty">No knowledge base articles available.</div></div>`;
    shadowRoot.appendChild(empty);
    return;
  }

  const allArticles = data.articles.slice(0, config.maxItems);

  const wrapper = document.createElement("div");
  wrapper.className = "ss-widget ss-kb";

  // ── List view container ────────────────────────────────────────────
  const listView = document.createElement("div");
  listView.className = "ss-kb-view ss-kb-list-view";

  // Header
  const header = document.createElement("div");
  header.className = "ss-kb-header";
  header.innerHTML = `<h2 class="ss-kb-heading">Knowledge Base</h2>`;
  listView.appendChild(header);

  // Search
  if (config.search) {
    const searchBox = document.createElement("div");
    searchBox.className = "ss-kb-search";
    searchBox.innerHTML = `${searchIcon}<input type="text" class="ss-kb-search-input" placeholder="Search articles..." />`;
    listView.appendChild(searchBox);
  }

  // Article grid
  const gridEl = document.createElement("div");
  gridEl.className = "ss-kb-results";
  renderArticleGrid(gridEl, allArticles, "");
  listView.appendChild(gridEl);

  // Footer (list view)
  if (config.branding) {
    const footer = document.createElement("div");
    footer.innerHTML = `<div class="ss-footer">
      <a href="https://shipstar.ai" target="_blank" rel="noopener noreferrer">${shipstarIcon} Powered by Shipstar</a>
    </div>`;
    listView.appendChild(footer);
  }

  wrapper.appendChild(listView);

  // ── Detail view container ──────────────────────────────────────────
  const detailView = document.createElement("div");
  detailView.className = "ss-kb-view ss-kb-detail-view ss-kb-hidden";
  wrapper.appendChild(detailView);

  // Detail footer
  const detailFooter = document.createElement("div");
  detailFooter.className = "ss-kb-detail-footer";
  if (config.branding) {
    detailFooter.innerHTML = `<div class="ss-footer">
      <a href="https://shipstar.ai" target="_blank" rel="noopener noreferrer">${shipstarIcon} Powered by Shipstar</a>
    </div>`;
  }

  shadowRoot.appendChild(wrapper);

  // ── Navigation helpers ─────────────────────────────────────────────

  function navigateToArticle(slug: string): void {
    // If the host page handles navigation (e.g. Next.js router), delegate
    if (config.onNavigate) {
      config.onNavigate(slug);
      return;
    }
    showArticle(slug);
  }

  function navigateToList(): void {
    if (config.onNavigate) {
      // Navigate to the listing page by passing empty string
      config.onNavigate("");
      return;
    }
    showList();
  }

  function showList(): void {
    listView.classList.remove("ss-kb-hidden");
    detailView.classList.add("ss-kb-hidden");
    wrapper.scrollTop = 0;
  }

  function showArticle(slug: string): void {
    const article = allArticles.find((a) => slugify(a.title) === slug);
    if (!article) return;

    detailView.innerHTML = renderArticleDetail(article, allArticles);
    detailView.appendChild(detailFooter);

    listView.classList.add("ss-kb-hidden");
    detailView.classList.remove("ss-kb-hidden");
    wrapper.scrollTop = 0;

    // Attach back button listener
    const backBtn = detailView.querySelector<HTMLButtonElement>(".ss-kb-back");
    if (backBtn) {
      backBtn.addEventListener("click", (e) => {
        e.preventDefault();
        navigateToList();
      });
    }

    // Attach related article click listeners
    detailView.querySelectorAll<HTMLAnchorElement>("[data-article-slug]").forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        const targetSlug = link.getAttribute("data-article-slug");
        if (targetSlug) navigateToArticle(targetSlug);
      });
    });

    // Attach detail search with dropdown
    const detailSearchInput = detailView.querySelector<HTMLInputElement>(".ss-kb-detail-search-input");
    const detailSearchWrap = detailView.querySelector<HTMLDivElement>(".ss-kb-detail-search");
    if (detailSearchInput && detailSearchWrap) {
      // Create dropdown
      const dropdown = document.createElement("div");
      dropdown.className = "ss-kb-search-dropdown ss-kb-hidden";
      detailSearchWrap.appendChild(dropdown);

      let debounceTimer: ReturnType<typeof setTimeout>;

      detailSearchInput.addEventListener("input", () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          const query = detailSearchInput.value.toLowerCase().trim();
          if (!query) {
            dropdown.classList.add("ss-kb-hidden");
            return;
          }
          const results = allArticles.filter(
            (a) =>
              a.title.toLowerCase().includes(query) ||
              a.content.toLowerCase().includes(query) ||
              (a.category && a.category.toLowerCase().includes(query))
          );
          if (results.length === 0) {
            dropdown.innerHTML = `<div class="ss-kb-dropdown-empty">No articles match "${escapeHtml(query)}"</div>`;
          } else {
            dropdown.innerHTML = results
              .map((a) => {
                const s = slugify(a.title);
                return `<a class="ss-kb-dropdown-item" href="#" data-article-slug="${escapeHtml(s)}">
                  <div class="ss-kb-dropdown-icon">${bookIcon}</div>
                  <div class="ss-kb-dropdown-text">
                    <span class="ss-kb-dropdown-title">${escapeHtml(a.title)}</span>
                    ${a.category ? `<span class="ss-kb-dropdown-cat">${escapeHtml(a.category)}</span>` : ""}
                  </div>
                </a>`;
              })
              .join("");
          }
          dropdown.classList.remove("ss-kb-hidden");
        }, 200);
      });

      detailSearchInput.addEventListener("focus", () => {
        if (detailSearchInput.value.trim()) {
          dropdown.classList.remove("ss-kb-hidden");
        }
      });

      // Navigate on dropdown item click
      dropdown.addEventListener("click", (e) => {
        const target = (e.target as HTMLElement).closest<HTMLAnchorElement>("[data-article-slug]");
        if (!target) return;
        e.preventDefault();
        const targetSlug = target.getAttribute("data-article-slug");
        detailSearchInput.value = "";
        dropdown.classList.add("ss-kb-hidden");
        if (targetSlug) navigateToArticle(targetSlug);
      });

      // Close dropdown on outside click
      wrapper.addEventListener("click", (e) => {
        if (!detailSearchWrap.contains(e.target as Node)) {
          dropdown.classList.add("ss-kb-hidden");
        }
      });
    }
  }

  // ── Event delegation for article card clicks ───────────────────────

  gridEl.addEventListener("click", (e) => {
    const target = (e.target as HTMLElement).closest<HTMLAnchorElement>("[data-article-slug]");
    if (!target) return;
    e.preventDefault();
    const slug = target.getAttribute("data-article-slug");
    if (slug) navigateToArticle(slug);
  });

  // ── Initial article (deep-link into a specific article on load) ────

  if (config.initialArticle) {
    showArticle(config.initialArticle);
  }

  // ── Search ─────────────────────────────────────────────────────────

  if (config.search) {
    const input = shadowRoot.querySelector<HTMLInputElement>(".ss-kb-search-input");
    if (input) {
      let debounceTimer: ReturnType<typeof setTimeout>;
      input.addEventListener("input", () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          const query = input.value.toLowerCase().trim();
          const filtered = query
            ? allArticles.filter(
                (a) =>
                  a.title.toLowerCase().includes(query) ||
                  a.content.toLowerCase().includes(query) ||
                  (a.category && a.category.toLowerCase().includes(query))
              )
            : allArticles;
          renderArticleGrid(gridEl, filtered, query);
        }, 200);
      });

      // Auto-focus search field when widget loads on the list view
      if (!config.initialArticle) {
        input.focus();
      }
    }
  }
}
